import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from './client'

// --- Types ---

export type ExperimentStatus = 'proposed' | 'approved' | 'running' | 'evaluating' | 'evaluated' | 'cancelled' | 'rolled_back' | 'auto_approved' | 'shadow'
export type ExperimentType = 'bid_adjustment' | 'keyword_promotion' | 'keyword_addition' | 'keyword_pause' | 'negative_addition' | 'budget_adjustment'
export type RiskLevel = 'low' | 'medium' | 'high'
export type ExperimentOutcome = 'success' | 'neutral' | 'failure'

export interface Experiment {
  id: number
  profileId: number
  experimentType: ExperimentType
  campaignId: number | null
  keywordId: number | null
  hypothesis: string
  expectedOutcome: Record<string, unknown>
  riskLevel: RiskLevel
  changeDetails: Record<string, unknown>
  measurementDays: number
  status: ExperimentStatus
  source: string
  proposedAt: string | null
  approvedAt: string | null
  startedAt: string | null
  evaluationDueAt: string | null
  evaluatedAt: string | null
  cancelledAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ExperimentBaseline {
  id: number
  experimentId: number
  baselineStart: string
  baselineEnd: string
  entityType: string
  entityId: number
  impressions: number
  clicks: number
  spend: string
  sales: string
  orders: number
  acos: string | null
  roas: string | null
  ctr: string | null
  cvr: string | null
  cpc: string | null
  averageBid: string | null
}

export interface ExperimentEvaluation {
  id: number
  experimentId: number
  measurementStart: string
  measurementEnd: string
  impressions: number
  clicks: number
  spend: string
  sales: string
  orders: number
  acos: string | null
  roas: string | null
  outcome: ExperimentOutcome
  confidenceScore: number
  deltaImpressionsPercent: string | null
  deltaClicksPercent: string | null
  deltaSpendPercent: string | null
  deltaSalesPercent: string | null
  deltaAcosAbsolute: string | null
  deltaRoasPercent: string | null
  aiSummary: string | null
}

export interface ExperimentDetail {
  experiment: Experiment
  baseline: ExperimentBaseline | null
  evaluation: ExperimentEvaluation | null
  shadowResult: Record<string, unknown> | null
}

export interface ExperimentStats {
  total: number
  running: number
  pending: number
  dueSoon: number
  successRate30d: number
  successRate90d: number
  shadowAccuracy: number
}

export interface TimelineEvent {
  timestamp: string | null
  event: string
  details?: unknown
}

export interface ExperimentLearning {
  id: number
  profileId: number
  experimentType: ExperimentType
  learningType: string
  description: string
  evidence: Record<string, unknown>
  proposedChange: Record<string, unknown>
  status: 'proposed' | 'approved' | 'applied' | 'rejected' | 'superseded'
  createdAt: string
  approvedAt: string | null
  appliedAt: string | null
}

export interface ExperimentConfig {
  id: number
  profileId: number
  mode: string
  loopEnabled: number
  pauseAll: number
  autoApproveEnabled: number
  maxConcurrentPerKeyword: number
  maxConcurrentPerCampaign: number
  maxConcurrentPerProfile: number
  maxNewPerDay: number
  claudeBudgetEurMonth: string
  updatedAt: string
}

export interface ConfidenceLevels {
  currentMode: string
  evaluatedTotal: number
  successRate90d: number
  shadowAccuracy: number
  nextLevel: string | null
  canUpgrade: boolean
  criteria: {
    lowRiskAuto: { required: number; current: number; successRequired: number; successCurrent: number }
    fullAuto: { required: number; current: number; successRequired: number; successCurrent: number }
  }
}

/**
 * M20-UI (Scheibe F): Die ART des Ausgangs, den ein Mensch anfassen muss.
 * Bewusst vom Backend BENANNT statt hier rekonstruiert — die M20-UI-Luecke
 * entstand gerade daraus, dass die Information verschachtelt in `execution` lag.
 *
 * 'retry-safe'      INVARIANTE: der vorherige Amazon-Write hat nachweislich NICHT
 *                   stattgefunden (der Guard sitzt vor dem Dispatch).
 * 'unsafe-to-retry' INVARIANTE: der Amazon-Ausgang ist UNBEKANNT — ein Wiederholen
 *                   koennte den nicht idempotenten Vorgang doppeln.
 *
 * Was der Marker NICHT aussagt: dass es einen ausfuehrbaren Wiederholungsweg gibt.
 * Den gibt es derzeit nirgends — wie ein kuenftiger Vorgang revalidiert, ist offen
 * (D-A5). Hier stand frueher „er blockt erneut, solange die Abweichung besteht";
 * das ist seit der D5-Ruecknahme falsch, weil `/execute/` fuer ein geparktes
 * Experiment gar nicht bis M20 kommt, sondern vorher am Status-Gate scheitert.
 * Herleitung: `.superpowers/review/scheibe-f/durchgang-7/codex-review-VERDIKT.md`.
 */
export type ManualAction = 'retry-safe' | 'unsafe-to-retry'

/**
 * Spiegel von `ExecuteResult` (`backend/src/modules/experiments/workers/executor.ts:122`).
 * Task 2c: `mode` ist dort NICHT optional, und `promotionEventId` fehlte hier ganz.
 */
export interface ExperimentExecution {
  success: boolean
  mode: 'live' | 'shadow'
  error?: string
  unresolved?: boolean
  policyDenial?: boolean
  reconcileRequired?: boolean
  promotionEventId?: number
  persistenceWarning?: string
}

/**
 * Antwortform der beiden Einzel-Schreibrouten `/approve/` und `/execute/` im 200-Fall.
 *
 * `execution` ist VERPFLICHTEND — am Backend nachgezaehlt statt angenommen: `/approve/`
 * hat drei 200-Pfade (Executor hat geworfen · Status-Reload gescheitert · Normalfall),
 * `/execute/` zwei (Status-Reload gescheitert · Normalfall). Alle fuenf setzen das Feld;
 * einen 200 ohne `execution` gibt es nicht. `experiment` dagegen ist auf den
 * Reload-Pfaden `null` — deshalb dort und nur dort die Nullbarkeit.
 */
export interface ApproveExperimentResponse {
  experiment: Experiment | null
  execution: ExperimentExecution
  manualAction?: ManualAction
  persistenceWarning?: string
}

export interface BulkApproveItem {
  id: number
  success: boolean
  executed: boolean
  mode?: 'live' | 'shadow'
  error?: string
  executionError?: string
  persistenceWarning?: string
  unresolved?: boolean
  policyDenial?: boolean
  reconcileRequired?: boolean
  manualAction?: ManualAction
}

export interface BulkApproveResponse {
  results: BulkApproveItem[]
  approved: number
  failed: number
  executed: number
  shadow: number
  executionFailed: number
  manualActionRequired: number
  retrySafe: number
  unsafeToRetry: number
}

/**
 * Task 2c (Scheibe F): Nach einer Mutation an EINEM Experiment muessen alle Sichten
 * neu laden, die dieses Experiment zeigen — auch die Detailseite.
 *
 * WARUM ES OHNE DIESE STELLE BRICHT: TanStack Query gleicht Query-Keys SEGMENTWEISE
 * ab. `['experiments']` trifft `['experiments', filters]` (die Liste), aber NICHT den
 * Singular `['experiment', id]` — und genau den liest die Detailseite (ebenso
 * `['experiment-timeline', id]`). Folge vor dieser Korrektur: Der Operator genehmigt auf
 * der Detailseite, das Backend setzt Status und Zeitstempel — und die Seite zeigt weiter
 * den alten Stand. Ausgerechnet in der Lage, in der er auf einen ungeklaerten
 * Amazon-Zustand reagiert.
 *
 * EINE Stelle statt mehrerer Kopien: Alle von der Detailseite konsumierten Mutations-Hooks
 * hatten dieselbe Luecke — damals vier, seit der Entfernung von `useExecuteExperiment`
 * (siehe Kasten oben) drei. Gleichlautende Bloecke waeren ebenso viele Stellen, an denen
 * die naechste Sicht wieder vergessen werden kann.
 *
 * NICHT fuer die Bulk-Hooks: Dort gibt es keine per-`id`-Detailquery, die stehen
 * bleiben koennte.
 */
function invalidateExperimentViews(qc: QueryClient, id: number): void {
  qc.invalidateQueries({ queryKey: ['experiments'] })
  qc.invalidateQueries({ queryKey: ['experiment-stats'] })
  qc.invalidateQueries({ queryKey: ['experiment', id] })
  qc.invalidateQueries({ queryKey: ['experiment-timeline', id] })
}

// --- Experiment Hooks ---

export function useExperiments(filters?: Record<string, string | number>) {
  const params = new URLSearchParams()
  if (filters) {
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.set(k, String(v))
    })
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return useQuery({
    queryKey: ['experiments', filters],
    queryFn: () => api.get<{ experiments: Experiment[]; total: number }>(`/experiments/${qs}`),
  })
}

export function useExperiment(id: number) {
  return useQuery({
    queryKey: ['experiment', id],
    queryFn: () => api.get<ExperimentDetail>(`/experiments/${id}/`),
    enabled: id > 0,
  })
}

export function useExperimentStats() {
  return useQuery({
    queryKey: ['experiment-stats'],
    queryFn: () => api.get<{ stats: ExperimentStats }>('/experiments/stats/'),
  })
}

export function useExperimentTimeline(id: number) {
  return useQuery({
    queryKey: ['experiment-timeline', id],
    queryFn: () => api.get<{ timeline: TimelineEvent[] }>(`/experiments/${id}/timeline/`),
    enabled: id > 0,
  })
}

export function useCreateExperiment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post<{ experiment: Experiment }>('/experiments/', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['experiments'] }); qc.invalidateQueries({ queryKey: ['experiment-stats'] }) },
  })
}

export function useApproveExperiment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<ApproveExperimentResponse>(`/experiments/${id}/approve/`),
    onSuccess: (_data, id) => invalidateExperimentViews(qc, id),
  })
}

/*
 * ⛔ HIER STAND `useExecuteExperiment()` — ABSICHTLICH ENTFERNT (2026-08-04).
 *    Lies das, bevor du einen Hook auf `/experiments/:id/execute/` neu anlegst.
 *
 * Der Hook wurde in Scheibe F gebaut, um einem von M20 geparkten Experiment einen
 * „Erneut ausfuehren"-Knopf zu geben. Dieser Knopf konnte nie funktionieren
 * (Codex-Durchgang 3, Finding 1; User-Entscheidung 2026-08-04):
 *
 *   1. Nach einem M20-Park steht das Experiment auf `running`
 *      (`backend/tests/experiments/reconcile-required-e2e.test.ts` — der Test heisst
 *      woertlich „geparkt auf running, nicht wieder ausloesbar")
 *   2. `executeExperiment()` akzeptiert AUSSCHLIESSLICH `approved` und `auto_approved`
 *      (`backend/src/modules/experiments/workers/executor.ts:393`)
 *   3. Die Route mappt das auf **HTTP 409 „Ungueltiger Status: running"**
 *      (`backend/src/modules/experiments/routes.ts:290`)
 *
 * Das ist kein Versehen des Backends, sondern ein Schutz aus Scheibe E: `executor.ts:545 ff.`
 * setzt den Claim bei `reconcileRequired` bewusst NICHT zurueck, weil das Experiment sonst
 * „SOFORT WIEDER AUSLOESBAR" waere — per Route, per Bulk, per Auto-Approve.
 *
 * Warum der Hook nicht bloss ohne Aufrufer stehenblieb: Das war der erste Anlauf, und er
 * wiederholte genau die Konstruktion, die in diesem Modul schon einmal versagt hat — ein
 * Warnkommentar neben einem weiterhin ausfuehrbaren Artefakt (Codex-Durchgang 4, Finding 3:
 * derselbe Fehler in der Plandatei). Ein Kommentar haelt niemanden auf, ein fehlender Hook
 * schon. Das Wissen bleibt hier und in `frontend/src/wiki/10-sicherheit.md`; die
 * ausfuehrbare Flaeche ist weg.
 *
 * Die Route selbst ist real und fuer `approved`/`auto_approved` voellig legitim — wer sie
 * dafuer braucht, legt einen Hook mit eigenen Tests an. Ein tragfaehiger Retry fuer
 * GEPARKTE Experimente braucht dagegen ein durables, serverseitig lesbares Verdikt:
 * Registerpunkt **D-A5**, offen.
 */

export function useRejectExperiment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => api.post<{ experiment: Experiment }>(`/experiments/${id}/reject/`, { reason }),
    onSuccess: (_data, { id }) => invalidateExperimentViews(qc, id),
  })
}

/**
 * Antwortform von `/experiments/:id/rollback/` im 200-Fall.
 *
 * INVARIANTE: Ein 200 heisst „der Amazon-Revert ist committet". Ein zusaetzliches
 * `persistenceWarning` heisst: lokale Verbuchung, Audit-Eintrag oder Status-Reload sind
 * gescheitert — der Serverzustand, den die Oberflaeche gleich nachlaedt, kann falsch sein.
 * Ein 200 ist damit NICHT gleichbedeutend mit „alles in Ordnung".
 *
 * `experiment` ist auf BEIDEN 200-Pfaden nullbar — am Backend nachgezaehlt, nicht
 * angenommen: Status-Reload gescheitert (`experiments/routes.ts` ~356) und Normalfall
 * (`?? null`, ebd. ~363).
 *
 * Vorher war das als `{ experiment: Experiment }` typisiert. Damit war beides unsichtbar,
 * und ein erfolgreicher Rollback mit gescheiterter Persistenz sah aus wie ein glatter
 * Erfolg (Codex-Durchgang 8, Finding 1).
 */
/**
 * Die URSACHE einer Persistenzwarnung — strukturiert, damit die Oberflaeche sie nicht aus
 * dem Freitext lesen muss (Codex-Durchgang 9, Finding 1).
 *
 * 'status-reload'     INVARIANTE: Der Amazon-Write UND die lokale Verbuchung sind durch;
 *                     nur das unmittelbare Nachladen des Status schlug fehl. Ein
 *                     erfolgreicher Refetch erledigt das — der Hinweis darf dann enden.
 * 'local-persistence' INVARIANTE: Der Amazon-Write ist durch, aber lokale Verbuchung oder
 *                     Audit-Eintrag sind unvollstaendig. Das loest KEIN Refetch auf.
 *
 * Bei Ueberlagerung gilt fail-closed 'local-persistence' — die Ursache, die sich nicht von
 * selbst aufloest. Auf `/rollback/` entscheidet das Backend so (`experiments/routes.ts`,
 * Status-Reload-Pfad); auf den Approve-Wegen leitet es `persistenzBefundAusApprove()`
 * unten nach derselben Regel ab.
 *
 * Hiess bis Scheibe M `RollbackWarningKind` — der Begriff gilt fuer alle Schreibwege, nicht
 * nur fuer den Rollback. Kein Alias: zwei Namen fuer denselben Begriff waeren genau die
 * Doppeldeutigkeit, die dieses Modul beseitigt.
 */
export type PersistenceWarningKind = 'local-persistence' | 'status-reload'

export interface RollbackExperimentResponse {
  experiment: Experiment | null
  persistenceWarning?: string
  warningKind?: PersistenceWarningKind
}

/** Eine anzeigefertige Persistenzwarnung: was gilt (`art`) und der Backend-Freitext dazu. */
export interface PersistenzBefund {
  /** Backend-Freitext — AUSSCHLIESSLICH als eingeklappte Diagnose anzuzeigen. */
  text: string
  art: PersistenceWarningKind
}

/**
 * Leitet die Ursache einer Persistenzwarnung aus `/approve/` aus der STRUKTUR der Antwort
 * ab — nicht aus ihrem Text.
 *
 * WARUM DAS OHNE `warningKind` GEHT: Das Backend legt die beiden Ursachen in zwei
 * verschiedene Felder (`backend/src/modules/experiments/routes.ts`, hier nur gelesen):
 *
 *   `execution.persistenceWarning`  reicht der Executor aus der apply-bridge durch
 *                                   (`workers/executor.ts:625/663`) ⇒ lokale Verbuchung
 *                                   oder Audit unvollstaendig.
 *   top-level `persistenceWarning`  setzt AUSSCHLIESSLICH der `catch` des Status-Reloads
 *                                   (`routes.ts:243`) ⇒ nur das Nachladen scheiterte.
 *
 * FAIL-CLOSED bei Ueberlagerung: `local-persistence` gewinnt. Ein Refetch loest den
 * Reload-Anteil auf, die Verbuchungsluecke nicht — also gilt die haertere Ursache.
 *
 * ZUM TEXT: `routes.ts:243` verkettet die Execution-Warnung heute in den top-level-Text
 * hinein. Wuerde hier einfach der top-level-Text genommen, ginge die Execution-Warnung
 * STILL verloren, sobald das Backend das Verketten aufgibt — der Operator saehe dann nur
 * noch den Reload-Teil. Deshalb wird zusammengesetzt, wenn er nicht schon drinsteht.
 * Das ist KEINE Freitext-Auswertung: Ueber die Ursache entscheidet allein die Struktur;
 * geprueft wird nur, ob ein Text bereits in einem anderen enthalten ist.
 */
export function persistenzBefundAusApprove(a: ApproveExperimentResponse): PersistenzBefund | null {
  const ausfuehrung = a.execution?.persistenceWarning
  const oben = a.persistenceWarning
  if (!ausfuehrung) return oben ? { text: oben, art: 'status-reload' } : null
  const text = oben && !oben.includes(ausfuehrung) ? `${ausfuehrung}; ${oben}` : (oben ?? ausfuehrung)
  return { text, art: 'local-persistence' }
}

/**
 * Dasselbe fuer einen Eintrag aus `/bulk-approve/`.
 *
 * INVARIANTE: Dort gibt es KEINEN Status-Reload-Zweig — die Route reicht ausschliesslich
 * die Execution-Warnung in den Eintrag durch (`routes.ts:431`). Die Ursache ist deshalb
 * immer `local-persistence`, und ein Refetch loest sie nie auf.
 *
 * Eigene Funktion statt eines gemeinsamen Aufrufs: Das Feld heisst hier gleich, bedeutet
 * aber etwas anderes. Eine generische Fassung muesste am Aufrufort umnormalisiert werden —
 * genau die stille Voraussetzung, die spaeter jemand falsch liest.
 */
export function persistenzBefundAusBulk(e: BulkApproveItem): PersistenzBefund | null {
  return e.persistenceWarning ? { text: e.persistenceWarning, art: 'local-persistence' } : null
}

export function useRollbackExperiment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => api.post<RollbackExperimentResponse>(`/experiments/${id}/rollback/`, { reason }),
    onSuccess: (_data, { id }) => invalidateExperimentViews(qc, id),
  })
}

export function useBulkApproveExperiments() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (experimentIds: number[]) => api.post<BulkApproveResponse>('/experiments/bulk-approve/', { experimentIds }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['experiments'] }); qc.invalidateQueries({ queryKey: ['experiment-stats'] }) },
  })
}

export function useBulkRejectExperiments() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ experimentIds, reason }: { experimentIds: number[]; reason?: string }) => api.post('/experiments/bulk-reject/', { experimentIds, reason }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['experiments'] }); qc.invalidateQueries({ queryKey: ['experiment-stats'] }) },
  })
}

export function useGenerateHypotheses() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dryRun: boolean = false) => api.post(`/experiments/generate-hypotheses/?dry_run=${dryRun}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['experiments'] }); qc.invalidateQueries({ queryKey: ['experiment-stats'] }) },
  })
}

// --- Learning Hooks ---

export function useLearningEvolution(profileId?: number) {
  const qs = profileId ? `?profileId=${profileId}` : ''
  return useQuery({
    queryKey: ['learning-evolution', profileId],
    queryFn: () => api.get<{ learnings: ExperimentLearning[] }>(`/learning/evolution/${qs}`),
  })
}

export function useSuccessRates(profileId?: number) {
  const qs = profileId ? `?profileId=${profileId}` : ''
  return useQuery({
    queryKey: ['learning-success-rates', profileId],
    queryFn: () => api.get<{ rates: Array<Record<string, unknown>> }>(`/learning/success-rates/${qs}`),
  })
}

export function useConfidenceLevels(profileId?: number) {
  const qs = profileId ? `?profileId=${profileId}` : `?profileId=1`
  return useQuery({
    queryKey: ['learning-confidence', profileId],
    queryFn: () => api.get<ConfidenceLevels>(`/learning/confidence-levels/${qs}`),
  })
}

export function useApproveLearning() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/learning/evolution/${id}/approve/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['learning-evolution'] }),
  })
}

export function useRejectLearning() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/learning/evolution/${id}/reject/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['learning-evolution'] }),
  })
}

// --- Settings Hooks ---

export function useExperimentMode(profileId?: number) {
  const qs = profileId ? `?profileId=${profileId}` : `?profileId=1`
  return useQuery({
    queryKey: ['experiment-mode', profileId],
    queryFn: () => api.get<{ config: ExperimentConfig; levels: ConfidenceLevels }>(`/settings/experiment-mode/${qs}`),
  })
}

export function useUpdateExperimentMode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.put<{ config: ExperimentConfig }>('/settings/experiment-mode/', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['experiment-mode'] }); qc.invalidateQueries({ queryKey: ['learning-confidence'] }) },
  })
}

// --- Hanging Leases Hooks ---

export interface HangingLease {
  id: number
  waveId: number
  keywordId: number | null
  purpose: 'experiment:bid' | 'experiment:state' | null
  baseline: Record<string, unknown>
  acquiredAt: string
  // `null`, wenn der Erwerbszeitpunkt der Lease nicht lesbar war. Die Seite
  // faellt dann auf den Hinweis ohne Schaltflaeche zurueck — kein Termin
  // heisst keine Aktion, nicht "sofort erlaubt".
  resolvableAt: string | null
  // Ab wann die ZWEITE Pruefung angenommen wird — created_at der ersten
  // Baseline-Beobachtung + Karenz. `null`, solange keine Beobachtung vorliegt.
  secondResolvableAt: string | null
}

export interface ResolveHangingLeaseResponse {
  leaseId: number
  leaseStatus: 'active' | 'voided'
  evidence: {
    readAt: string
    amazonKeywordId: string
    verdict: 'forward' | 'baseline'
    detail?: string
  }
}

export interface HangingLeasesResponse {
  leases: HangingLease[]
  // Die Uhr des SERVERS zum Zeitpunkt der Antwort. Gegen sie gelten die Termine
  // der Liste — nicht gegen die Uhr des Rechners, auf dem der Browser laeuft.
  // Fehlt das Feld oder ist es `null` (aeltere Serverversion, unlesbare
  // DB-Zeit), faellt die Seite auf ihre eigene Uhr zurueck.
  serverTime?: string | null
}

export function listHangingLeases() {
  return api.get<HangingLeasesResponse>('/experiments/hanging-leases/')
}

export function resolveHangingLease(leaseId: number) {
  return api.post<ResolveHangingLeaseResponse>(`/experiments/hanging-leases/${leaseId}/resolve/`)
}

/**
 * Der Versatz zwischen der Uhr dieses Browsers und der des Servers, in
 * Millisekunden. `null` heisst „unbekannt" — dann wird nicht geraten, sondern
 * die eigene Uhr benutzt.
 *
 * DER ZEITPUNKT DER BERECHNUNG IST DER GANZE PUNKT: der Versatz wird EINMAL je
 * Antwort gebildet, naemlich hier beim Empfang. Rechnete man ihn beim Rendern,
 * waere er giftig — `serverTime` steht dann fest, `Date.now()` laeuft weiter,
 * und `Date.now() + (serverTime - Date.now())` ergibt immer wieder den
 * Empfangszeitpunkt. Die Uhr der Seite stuende still, und eine Frist, die
 * waehrend der geoeffneten Seite ablaeuft, liefe nie ab.
 *
 * Die Netzlaufzeit der Antwort steckt mit im Versatz: die gemessene Serverzeit
 * ist bei ihrer Ankunft schon etwas alt, die Seite geht also minimal nach. Das
 * ist die unbedenkliche Richtung — die Schaltflaeche erscheint eher zu spaet
 * als zu frueh — und bei einer Karenz von 14 Minuten belanglos.
 */
function versatzZumServer(serverTime: string | null | undefined): number | null {
  if (!serverTime) return null
  const serverMs = Date.parse(serverTime)
  if (!Number.isFinite(serverMs)) return null
  return serverMs - Date.now()
}

export function useHangingLeases() {
  return useQuery({
    queryKey: ['hanging-leases'],
    queryFn: async () => {
      const antwort = await listHangingLeases()
      return { ...antwort, offsetMs: versatzZumServer(antwort?.serverTime) }
    },
    // Die Termine dieser Liste laufen im Sekundentakt ab. Ohne regelmaessiges
    // Nachladen bliebe eine Lease, deren Frist waehrend der geoeffneten Seite
    // ablaeuft, bis zum naechsten Reload gesperrt — der Server wuerde sie
    // laengst pruefen. Eine Minute ist grob genug, um nicht zu pollen, und fein
    // genug fuer eine Frist von 14 Minuten.
    refetchInterval: 60_000,
  })
}

export function useResolveHangingLease() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => resolveHangingLease(id),
    // `onSettled`, NICHT `onSuccess`: der wichtigste Zustandswechsel dieses
    // Weges kommt als FEHLER zurueck. Die erste Baseline-Beobachtung wird
    // geschrieben und dann mit 409 beantwortet — invalidierte man nur bei
    // Erfolg, bliebe `secondResolvableAt` im Zwischenspeicher auf `null`, die
    // Schaltflaeche erschiene sofort wieder, und der Operator koennte
    // unbegrenzt weiterklicken. Genau das soll diese Aenderung verhindern.
    onSettled: () => qc.invalidateQueries({ queryKey: ['hanging-leases'] }),
  })
}

